import { UseFormReturn } from "react-hook-form";
import { Inputs } from "../useUpsertProductForm";
import { RadioGroup } from "../../RadioGroup";
import Input from "../../Input";
import { stringToCurrency } from "~/utils/stringConversion";
import { Select } from "../../Select";
import { Checkbox } from "../../Checkbox";
import Link from "next/link";
import { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { OfferLawsModal } from "../../OfferLawsModal";

export const validateOfferEndDate =
  (offerStartDate?: string) => (value: string) => {
    const startDate = dayjs(offerStartDate ?? "").startOf("day");
    const endDate = dayjs(value).endOf("day");

    if (endDate.isBefore(startDate))
      return "Tilbuddet kan ikke slutte før det starter";

    // TODO: Uncomment when the feature is needed again
    // const difference = startDate.diff(endDate, "day");
    // if (difference < -10)
    // return "Tilbuddet kan ikke vare mere end 10 dage i følge lovgivningen";
  };

export const getOfferPeriodText = (
  offerStartDate?: string,
  offerEndDate?: string
) => {
  if (!offerStartDate || !offerEndDate) return null;

  const today = dayjs().startOf("day");
  const startDate = dayjs(offerStartDate).startOf("day");
  const daysTillStart = startDate.diff(today, "day");

  if (daysTillStart === 1)
    return "Ikke aktivt: Tilbudsperioden starter i morgen";
  if (daysTillStart > 0)
    return `Ikke aktivt: Tilbudsperioden starter om ${daysTillStart} dage`;

  const endDate = dayjs(offerEndDate).endOf("day");
  const daysTillEnd = endDate.diff(today, "day");

  if (daysTillEnd === 0) return "Tilbudsperioden slutter i dag";
  if (daysTillEnd === 1) return "Tilbudsperioden slutter i morgen";
  if (daysTillEnd < 0) return "Ikke aktivt: Tilbudsperioden er overstået";

  return `Tilbudsperioden slutter om ${daysTillEnd} dage`;
};

type Props = {
  form: UseFormReturn<Inputs>;
};

export const OfferInputs = ({ form }: Props) => {
  const {
    control,
    register,
    watch,
    formState: { errors },
    resetField,
  } = form;

  const hasOffer = watch("hasOffer");
  const price = watch("price");
  const offerPrice = watch("offerPrice");
  const offerStartDate = watch("offerStartDate");
  const offerEndDate = watch("offerEndDate");
  const offerTitle = watch("offerTitle");

  const offerPeriodText = useMemo(
    () => getOfferPeriodText(offerStartDate, offerEndDate),
    [offerStartDate, offerEndDate]
  );

  const options = useMemo(() => {
    const options = [
      { label: "Tilbud", value: "Tilbud" },
      { label: "Åbningstilbud", value: "Åbningstilbud" },
      { label: "Fødselsdagstilbud", value: "Fødselsdagstilbud" },
      { label: "Restlager", value: "Restlager" },
    ];

    if (offerTitle) {
      const alreadyHasOption = options.find(
        (option) => option.value === offerTitle.value
      );
      if (alreadyHasOption) return options;

      options.push(offerTitle);
    }

    return options;
  }, [offerTitle?.value]);

  useEffect(() => {
    if (hasOffer === "no") {
      resetField("offerPrice");
      resetField("offerStartDate");
      resetField("offerEndDate");
      resetField("offerTitle");
      resetField("offerIsLegal");
    }
  }, [hasOffer]);

  return (
    <>
      <h1 className="text-3xl my-5">Tilbud</h1>
      <div className="mb-12">
        <p>
          Følgende felter skal udfyldes, hvis dit produkt skal på tilbud.
          <br />
          Når tilbudsperioden er ophørt, vil varen selv går tilbage til
          normalpris.
        </p>
      </div>

      <div className="mb-8">
        <label id="hasOfferLabel" className="block mb-2">
          Er dette produkt på tilbud?
        </label>
        <RadioGroup
          id="hasOffer"
          aria-describedby="hasOfferLabel"
          control={control}
        >
          <RadioGroup.Item value="yes">Ja</RadioGroup.Item>
          <RadioGroup.Item value="no">Nej</RadioGroup.Item>
        </RadioGroup>
      </div>

      {hasOffer === "no" ? null : (
        <>
          <div className="mb-12">
            <Input
              id="offerPrice"
              label="Tilbudspris:"
              placeholder="00,00"
              register={register}
              type="currency"
              error={errors.offerPrice}
              registerOptions={{
                valueAsNumber: true,
                required: "Tilbudspris er påkrævet",
                max: {
                  // istanbul ignore next
                  value: price ? price - 0.01 : 0,
                  message:
                    "Tilbudsprisen skal være lavere end produktets normale pris.",
                },
              }}
            />
            {price && offerPrice ? (
              <small className="text-secondary-600">
                Besparelse på {stringToCurrency({ amount: price - offerPrice })}
              </small>
            ) : null}
          </div>

          <div className="mb-12">
            <Input
              id="offerStartDate"
              label="Tilbuddet starter:"
              placeholder="01/01-2000"
              type="date"
              register={register}
              error={errors.offerStartDate}
              registerOptions={{
                required: "Dato er påkrævet",
              }}
            />
            {offerPeriodText ? (
              <small className="text-secondary-600 font-bold">
                {offerPeriodText}
              </small>
            ) : null}
          </div>

          <Input
            id="offerEndDate"
            label="Tilbuddet slutter:"
            placeholder="01/01-2000"
            type="date"
            register={register}
            className="mb-12"
            error={errors.offerEndDate}
            registerOptions={{
              required: "Dato er påkrævet",
              // istanbul ignore next
              validate: validateOfferEndDate(offerStartDate),
            }}
          />

          <div className="mb-12">
            <Select
              control={control}
              id="offerTitle"
              label="Tilbudstype:"
              validation={{ required: "Tilbudstype er påkrævet" }}
              options={options}
              creatable
            />
            <small className="text-secondary-600">
              Vælg en tilbudstype fra listen eller skriv en ny
            </small>
          </div>

          <div className="mb-12">
            <Checkbox
              control={control}
              id="offerIsLegal"
              label={
                <span>
                  Dette tilbud lever op til{" "}
                  <Link
                    className="underline"
                    target="_blank"
                    href="https://www.forbrug.dk/emner/penge-og-forsikring/priser-tilbud-og-gebyrer/regler-tilbud-og-udsalg-foer-og-nu-priser/"
                  >
                    dansk lovgivning om tilbudsvarer
                  </Link>
                </span>
              }
              validation={{
                required:
                  "Du skal acceptere at tilbuddet lever op til lovgivning",
              }}
            />
            <small>
              <OfferLawsModal />
            </small>
          </div>
        </>
      )}
    </>
  );
};
